/* eslint-disable react/no-multi-comp */
import React from 'react'
import { FormattedMessage } from 'react-intl'
import {
    isoToFormattedLocalDayMonth,
    unixToFormattedLocalDayMonth,
    unixToFormattedLocalTime,
} from '@wotfe/formatters'
import moment from 'moment'

const emsp = <span>&emsp;</span>
const nbsp = <span>&nbsp;</span>

export function getSecondsFormat(seconds) {
    return (
        <FormattedMessage
            id='global.time_left.seconds'
            defaultMessage='{seconds}{nbsp}с'
            values={{
                seconds: seconds.toString(),
                nbsp, // we totally need whitespace between
            }}
        />
    )
}

export function getMinutesAndSecondsFormat(minutes, seconds) {
    return (
        <FormattedMessage
            id='global.time_left.minutes_and_seconds'
            defaultMessage='{minutes}{nbsp}м {seconds}{nbsp}с'
            values={{
                minutes: minutes.toString(),
                seconds: seconds.toString(),
                nbsp, // we totally need whitespace between
            }}
        />
    )
}

export function getMinutesFormat(minutes) {
    return (
        <FormattedMessage
            id='global.time_left.minutes'
            defaultMessage='{minutes}{nbsp}м'
            values={{
                minutes,
                nbsp,
            }}
        />
    )
}

export function getHoursFormat(hours) {
    return (
        <FormattedMessage
            id='global.time_left.hours'
            defaultMessage='{hours}{nbsp}ч'
            values={{
                hours,
                nbsp,
            }}
        />
    )
}

export function getHoursAndMinutesFormat(hours, minutes) {
    return (
        <FormattedMessage
            id='global.time_left.hours_and_minutes'
            // hardcoded to make line breaking correct;
            // ' ' is to center top and bottom parts (&emsp; appears at the end of line in case of line
            // breaking)
            defaultMessage='{hours}{nbsp}ч {minutes}{nbsp}мин'
            values={{
                hours,
                minutes: minutes.toString(),
                nbsp,
            }}
        />
    )
}

export function getFullFormat(hours, minutes, seconds) {
    return (
        <FormattedMessage
            id='global.time_left.full'
            defaultMessage='{hours}{nbsp}ч{emsp}{minutes}{nbsp}м{emsp}{seconds}{nbsp}с'
            values={{
                hours,
                minutes: minutes.toString(),
                seconds: seconds.toString(),
                emsp,
                nbsp,
            }}
        />
    )
}

export function getYesterdayTime(date) {
    return (
        <FormattedMessage
            id='global.yesterday_date'
            defaultMessage='вчера в {time}'
            values={{
                time: date.format('LT'),
            }}
        />
    )
}

export function getTodayTime(date) {
    return (
        <FormattedMessage
            id='global.today_date'
            defaultMessage='Сегодня в {time}'
            values={{
                time: date.format('LT'),
            }}
        />
    )
}

export function getTomorrowTime(date) {
    return (
        <FormattedMessage
            id='global.tomorrow_date'
            defaultMessage='Завтра в {time}'
            values={{
                time: date.format('LT'),
            }}
        />
    )
}

export function getCustomDateTime(date) {
    return (
        <FormattedMessage
            id='global.custom_date'
            defaultMessage='{date} в {time}'
            values={{
                date: date.format('D MMMM'),
                time: date.format('LT'),
            }}
        />
    )
}

export function getDatePeriod(startDate, endDate) {
    return (
        <FormattedMessage
            id='global.today_date_period'
            defaultMessage='{startDate} - {endDate}'
            values={{
                startDate: isoToFormattedLocalDayMonth(startDate),
                endDate: isoToFormattedLocalDayMonth(endDate),
            }}
        />
    )
}

export function getDateTimePeriod(startDate, endDate) {
    return (
        <FormattedMessage
            id='global.today_date_time_period'
            defaultMessage='{startDate}, {startTime} - {endDate}, {endTime}'
            values={{
                startDate: unixToFormattedLocalDayMonth(startDate),
                startTime: unixToFormattedLocalTime(startDate),
                endDate: unixToFormattedLocalDayMonth(endDate),
                endTime: unixToFormattedLocalTime(endDate),
            }}
        />
    )
}

export function getDateTime(date) {
    return (
        <FormattedMessage
            id='global.today_date_time'
            defaultMessage='{date}, {time}'
            values={{
                date: unixToFormattedLocalDayMonth(date),
                time: unixToFormattedLocalTime(date),
            }}
        />
    )
}

function getTodayTimePeriod(startDate, endDate, twoLines = false) {
    return (
        <FormattedMessage
            id='global.today_time_period'
            defaultMessage='Cегодня{brTag} {timeStart} - {timeEnd}'
            values={{
                timeStart: startDate.format('LT'),
                timeEnd: endDate.format('LT'),
                brTag: twoLines ? <br/> : undefined,
            }}
        />
    )
}

function getYesterdayTimePeriod(startDate, endDate, twoLines = false) {
    return (
        <FormattedMessage
            id='global.yesterday_time_period'
            defaultMessage='Вчера{brTag} {timeStart} - {timeEnd}'
            values={{
                timeStart: startDate.format('LT'),
                timeEnd: endDate.format('LT'),
                brTag: twoLines ? <br/> : undefined,
            }}
        />
    )
}

function getYesterdayTodayTimePeriod(startDate, endDate, twoLines = false) {
    return (
        <FormattedMessage
            id='global.yesterday_today_time_period'
            defaultMessage='Вчера {timeStart} -{brTag} Сегодня {timeEnd}'
            values={{
                timeStart: startDate.format('LT'),
                timeEnd: endDate.format('LT'),
                brTag: twoLines ? <br/> : undefined,
            }}
        />
    )
}

function getTomorrowTimePeriod(startDate, endDate, twoLines = false) {
    return (
        <FormattedMessage
            id='global.tomorrow_time_period'
            defaultMessage='Завтра{brTag} {timeStart} - {timeEnd}'
            values={{
                timeStart: startDate.format('LT'),
                timeEnd: endDate.format('LT'),
                brTag: twoLines ? <br/> : undefined,
            }}
        />
    )
}

function getTodayTomorrowTimePeriod(startDate, endDate, twoLines = false) {
    return (
        <FormattedMessage
            id='global.today_tomorrow_time_period'
            defaultMessage='Сегодня {timeStart} -{brTag} Завтра {timeEnd}'
            values={{
                timeStart: startDate.format('LT'),
                timeEnd: endDate.format('LT'),
                brTag: twoLines ? <br/> : undefined,
            }}
        />
    )
}

function getCustomTimePeriod(startDate, endDate, twoLines = false) {
    return (
        <FormattedMessage
            id='global.custom_time_period'
            defaultMessage='{dateStart} {timeStart} -{brTag} {dateEnd} {timeEnd}'
            values={{
                dateStart: startDate.format('D MMMM'),
                timeStart: startDate.format('LT'),
                dateEnd: endDate.format('D MMMM'),
                timeEnd: endDate.format('LT'),
                brTag: twoLines ? <br/> : undefined,
            }}
        />
    )
}

function getCustomWithSameDayTimePeriod(startDate, endDate, twoLines = false) {
    return (
        <FormattedMessage
            id='global.custom_same_day_time_period'
            defaultMessage='{date} {brTag}{timeStart} - {timeEnd}'
            values={{
                date: startDate.format('D MMMM'),
                timeStart: startDate.format('LT'),
                timeEnd: endDate.format('LT'),
                brTag: twoLines ? <br/> : nbsp,
            }}
        />
    )
}

/**
 * @param endDate - moment object
 * DO NOT USE this helper if you need to insert a parameter inside of localized string
 */
export function getEndDateMessage(endDate) {

    if (isTodayDay(endDate)) {
        return getTodayTime(endDate)
    } else if (isTomorrowDay(endDate)) {
        return getTomorrowTime(endDate)
    }

    return getCustomDateTime(endDate)
}

export function getClosestDate(dates) {
    const nowTimestamp = Date.now()
    dates = dates.filter((date) => {
        return date > nowTimestamp
    })
    const sorted = dates.sort()

    return sorted[0]
}

export function isTodayDay(date) {
    const today = moment().startOf('day')
    return date.isSame(today, 'day')
}

export function isTomorrowDay(date) {
    const tomorrow = moment().add(1, 'days').startOf('day')
    return date.isSame(tomorrow, 'day')
}

export function isYesterdayDay(date) {
    const yesterday = moment().subtract(1, 'days').startOf('day')
    return date.isSame(yesterday, 'day')
}

export function getTimePeriod(startTimeStamp, endTimeStamp, twoLines = false, combineSameDayForCustom = false) {
    const today = moment().startOf('day')
    const tomorrow = moment().add(1, 'days').startOf('day')
    const yesterday = moment().subtract(1, 'days').startOf('day')
    const startDate = moment(startTimeStamp)
    const endDate = moment(endTimeStamp)

    if (startDate.isSame(today, 'day') && endDate.isSame(today, 'day')) {
        return getTodayTimePeriod(startDate, endDate, twoLines)
    } else if (startDate.isSame(tomorrow, 'day') && endDate.isSame(tomorrow, 'day')) {
        return getTomorrowTimePeriod(startDate, endDate, twoLines)
    } else if (startDate.isSame(yesterday, 'day') && endDate.isSame(yesterday, 'day')) {
        return getYesterdayTimePeriod(startDate, endDate, twoLines)
    } else if (startDate.isSame(yesterday, 'day') && endDate.isSame(today, 'day')) {
        return getYesterdayTodayTimePeriod(startDate, endDate, twoLines)
    } else if (startDate.isSame(today, 'day') && endDate.isSame(tomorrow, 'day')) {
        return getTodayTomorrowTimePeriod(startDate, endDate, twoLines)
    } else if (combineSameDayForCustom && startDate.isSame(endDate, 'day')) {
        return getCustomWithSameDayTimePeriod(startDate, endDate, twoLines)
    } else {
        return getCustomTimePeriod(startDate, endDate, twoLines)
    }
}

export function splitTime(milliseconds) {
    let time = parseInt(milliseconds / 1000)
    const seconds = parseInt(time % 60)
    const minutes = parseInt((time / 60) % 60)
    const hours = parseInt((time / (60 * 60)))
    return { hours, minutes, seconds }
}


export function getFormattedTime(milliseconds = 0, simple = false, full = false) {
    const { hours, minutes, seconds } = splitTime(milliseconds)
    let time
    if (hours) {
        if (simple) return getHoursAndMinutesFormat(hours, minutes)

        if (minutes) {
            if (seconds && full) {
                time = getFullFormat(hours, minutes, seconds)
            } else {
                time = getHoursAndMinutesFormat(hours, minutes)
            }
        } else {
            time = getHoursFormat(hours)
        }
    } else if (minutes) {
        if (seconds || simple) {
            time = getMinutesAndSecondsFormat(minutes, seconds)
        } else {
            time = getMinutesFormat(minutes)
        }
    } else {
        time = getSecondsFormat(seconds)
    }
    return time
}

export function getHoursMinutes(milliseconds = 0) {
    const { hours, minutes } = splitTime(milliseconds)
    if (hours) {
        return getHoursAndMinutesFormat(hours, minutes)
    } else if (minutes) {
        return getMinutesFormat(minutes)
    } else {
        return getMinutesFormat(1)
    }
}



// WEBPACK FOOTER //
// ./helpers/datetime.js